import {
  CartLineNode,
  getProductInventoryStatus,
  InventoryStatusCode,
  ShopifyCartItem,
  ShopifyVariant,
} from '@/lib/shopify';
import {
  convertCartLineItemPropertiesToAttributes,
  getCartLineItemProperty,
} from '@/utils/shopify';
import { CartItemProperty } from '@/core/constants';
import { normalizeToBoolean } from '@/utils/strings';
import {
  createAttributesMap,
  createGetPropertyFunction,
  calculateDisplayPrice,
  determineMaximumQuantity,
  isAddonForParent,
  fetchProductData,
  extractSubscriptionDetails,
  SubscriptionDetails,
  DisplayPrice,
  ExtendedCartProductTranslations,
  parseRubyHashToJson,
} from '@/lib/cart/helpers';
import { handleIncompatibleItems } from '@/lib/cart/incompatibleItems';
import { base64Encode } from '@/utils/helpers';
import { VariantOption } from '@/components/cart/VariantDropdownSelector/VariantDropdownSelector';
import { DiscountData } from '@/types/bfcmDiscount';
import { applyPromoDiscounts } from '@/lib/cart/bfcmHelper';

export type SerializedCartItemBase = {
  id: string;
  title: string;
  handle: string;
  tags: string[];
  productType: string;
  imageUrl: string;
  price: string;
  currency: string;
  quantity: number;
  subtotal: string;
  subtotalCurrency: string;
  variants: ShopifyVariant[];
  variantTitle: string;
  variantId: string;
  sku: string;
  isAddon: boolean;
  maximumQuantity?: number;
  savings?: string;
  subscriptionDetails?: SubscriptionDetails;
  translations?: ExtendedCartProductTranslations;
  isCrossSell?: boolean;
  variantsSet?: Array<VariantOption>;
  pdpUrl?: string;
  availableForSale?: boolean;
  currentlyNotInStock?: boolean;
  attributes: Array<{ key: string; value: string }>;
  ageRange?: string;
  inventoryStatus?: InventoryStatusCode | null;
  hasBfcmDiscount?: boolean;
  hasEvergreenDiscount?: boolean;
};

export type SerializedCartItem = SerializedCartItemBase & {
  addons: SerializedCartItemBase[];
};

/**
 * Creates a SerializedCartItemBase from a CartLineNode
 * @param node - The cart line node
 * @param itemPrice - The calculated display price
 * @param parentAttributes - The parent cart item attributes (for addons)
 * @param translationsMap - Map of product translations
 * @returns SerializedCartItemBase
 */
function createSerializedCartItemBase({
  node,
  itemPrice,
  isAddon,
  parentAttributes,
  translationsMap,
  variantsSetMap,
  pdpUrlsMap,
  ageRangesMap,
}: {
  node: CartLineNode;
  itemPrice: DisplayPrice;
  isAddon: boolean;
  parentAttributes?: Array<{ key: string; value: string }>;
  translationsMap?: Map<string, ExtendedCartProductTranslations>;
  variantsSetMap?: Map<string, Array<VariantOption>>;
  pdpUrlsMap?: Map<string, string>;
  ageRangesMap?: Map<string, string>;
}): SerializedCartItemBase {
  const attributes = node.attributes || [];
  const attributesMap = createAttributesMap(attributes);
  const getProperty = createGetPropertyFunction(attributesMap);
  const quantityLimitedToOne = getProperty('quantity_limited_to_one');
  const handle = node.merchandise.product?.handle || '';

  // Get orderMax from product metafield if available
  const orderMaxValue = node.merchandise.product?.metafieldOrderMax?.value;
  const orderMax = orderMaxValue ? parseInt(orderMaxValue, 10) : undefined;

  const unitPrice = parseFloat(itemPrice.price);
  const subtotal = (unitPrice * node.quantity).toFixed(2);

  // Get translations, variantsSet, pdpUrl, and ageRange for this product
  let translations: ExtendedCartProductTranslations | undefined = undefined;
  // Find the selected variant from the product's variants
  const selectedVariant = getSelectedVariant(node);

  const inventoryStatus = getProductInventoryStatus(
    node.merchandise.product ?? null,
    selectedVariant,
  );

  // Get translations, variantsSet, and pdpUrl for this product
  let variantsSet:
    | Array<{ name: string; imageUrl: string | null }>
    | undefined = undefined;
  let pdpUrl: string | undefined = undefined;
  let ageRange: string | undefined = undefined;
  if (node.merchandise.product?.id) {
    const shopifyProductIdB64 = base64Encode(node.merchandise.product.id);
    translations = translationsMap?.get(shopifyProductIdB64);
    variantsSet = variantsSetMap?.get(shopifyProductIdB64);
    pdpUrl = pdpUrlsMap?.get(shopifyProductIdB64);
    ageRange = ageRangesMap?.get(shopifyProductIdB64);
  }

  const variants = node.merchandise.product?.variants?.edges || [];
  const hasAvailableVariant = variants.some(
    (edge) => edge.node.availableForSale && !edge.node.currentlyNotInStock,
  );
  // Extract subscription details if this is a subscription product
  const subscriptionDetails = extractSubscriptionDetails(node);

  // Check if this is a subscription product and get bundleAsset from subscriptionsPayload
  let imageUrl = node.merchandise.image?.url || '';
  const subscriptionsPayload = getProperty(
    CartItemProperty.PlayKitSubscriptionSettings,
  );
  if (subscriptionsPayload) {
    const parsedPayload = parseRubyHashToJson<{ bundleAsset?: string }>(
      subscriptionsPayload,
      'subscriptionsPayload',
    );
    if (parsedPayload?.bundleAsset) {
      imageUrl = parsedPayload.bundleAsset;
    }
  }

  return {
    id: node.id,
    title: node.merchandise.product?.title || '',
    handle,
    imageUrl,
    price: itemPrice.price,
    currency: itemPrice.currency,
    quantity: node.quantity,
    subtotal: itemPrice.subtotal || subtotal,
    tags: node.merchandise.product?.tags || [],
    productType: node.merchandise.product?.productType || '',
    subtotalCurrency: node.cost.subtotalAmount.currencyCode,
    variants:
      node.merchandise.product?.variants?.edges.map((edge) => edge.node) || [],
    variantTitle: node.merchandise.title || '',
    variantId: node.merchandise.id || '',
    sku: node.merchandise.sku || '',
    maximumQuantity: determineMaximumQuantity(
      handle,
      quantityLimitedToOne,
      parentAttributes || [],
      orderMax,
    ),
    isAddon,
    translations,
    variantsSet,
    pdpUrl,
    availableForSale: hasAvailableVariant,
    currentlyNotInStock: !hasAvailableVariant,
    attributes,
    ...(ageRange && { ageRange }),
    ...(subscriptionDetails && { subscriptionDetails }),
    inventoryStatus,
  };
}

function getSelectedVariant(node: CartLineNode) {
  const edges = node.merchandise.product?.variants?.edges;

  const merchandiseVariant = edges?.find(
    (edge) => edge.node.id === node.merchandise.id,
  )?.node;

  if (merchandiseVariant?.availableForSale) return merchandiseVariant;

  const firstVariantAvailableForSale = edges?.find(
    (edge) => edge.node.availableForSale,
  )?.node;

  return firstVariantAvailableForSale || merchandiseVariant;
}

enum CartProductType {
  PlayKitSubscription = 'Play Kit Subscription',
  PlayKitSubscriptionBookBundleAddon = 'Play Kit Subscription Book Bundle Addon',
  PlayKitGiftSubscription = 'Play Kit Gift Subscription',
  SubscriptionPlan = 'Subscription Plan',
  CoursePack = 'Course Pack',
  Standalone = 'Standalone',
}

export function organizeCartItems(items: ShopifyCartItem[]) {
  type CartItemGroups = {
    playKitSubscriptionProduct: ShopifyCartItem | null;
    playKitSubscriptionBookBundleAddonProduct: ShopifyCartItem | null;
    playKitGiftSubscriptionProduct: ShopifyCartItem | null;
    subscriptionPlanProducts: ShopifyCartItem[];
    coursePackProducts: ShopifyCartItem[];
    standaloneProducts: ShopifyCartItem[];
  };

  const initialGroups: CartItemGroups = {
    playKitSubscriptionProduct: null,
    playKitSubscriptionBookBundleAddonProduct: null,
    playKitGiftSubscriptionProduct: null,
    subscriptionPlanProducts: [],
    coursePackProducts: [],
    standaloneProducts: [],
  };

  return items.reduce((groups, item) => {
    const productType = identifyProductType(item);

    switch (productType) {
      case CartProductType.PlayKitSubscription:
        groups.playKitSubscriptionProduct = item;
        break;

      case CartProductType.PlayKitSubscriptionBookBundleAddon:
        groups.playKitSubscriptionBookBundleAddonProduct = item;
        break;

      case CartProductType.PlayKitGiftSubscription:
        groups.playKitGiftSubscriptionProduct = item;
        break;

      case CartProductType.SubscriptionPlan:
        groups.subscriptionPlanProducts.push(item);
        break;

      case CartProductType.CoursePack:
        groups.coursePackProducts.push(item);
        break;

      case CartProductType.Standalone:
        groups.standaloneProducts.push(item);
        break;
    }

    return groups;
  }, initialGroups);
}

export function identifyProductType(item: ShopifyCartItem): CartProductType {
  const attributes = convertCartLineItemPropertiesToAttributes(item.properties);

  const conditions: [boolean, CartProductType][] = [
    [
      !!getCartLineItemProperty(
        CartItemProperty.PlayKitSubscriptionSettings,
        attributes,
      ),
      CartProductType.PlayKitSubscription,
    ],
    [
      !!getCartLineItemProperty(
        CartItemProperty.PlayKitGiftSubscriptionSettings,
        attributes,
      ),
      CartProductType.PlayKitGiftSubscription,
    ],
    [
      !!getCartLineItemProperty(
        CartItemProperty.SubscriptionPlanSettings,
        attributes,
      ),
      CartProductType.SubscriptionPlan,
    ],
    [
      normalizeToBoolean(
        getCartLineItemProperty(
          CartItemProperty.IsPlayKitSubscriptionBookBundleAddon,
          attributes,
        ),
      ),
      CartProductType.PlayKitSubscriptionBookBundleAddon,
    ],
    [
      normalizeToBoolean(
        getCartLineItemProperty(CartItemProperty.IsCoursePack, attributes),
      ),
      CartProductType.CoursePack,
    ],
  ];

  for (const [identifyingCondition, productType] of conditions) {
    if (identifyingCondition) {
      return productType;
    }
  }

  return CartProductType.Standalone;
}

/**
 * Serializes cart line edges into a friendly array of cart item objects with addons grouped.
 * @param {Array<{ node: CartLineNode }>} edges - The cart line edges from the cart response.
 * @param {string} locale - The locale for fetching product data.
 * @param discountData - Optional BFCM and evergreen discount data.
 * @param {boolean} skipIncompatibleItemsRemoval - If true, skips API calls to remove incompatible items.
 * @returns {Array<SerializedCartItem>} - Friendly cart items with addons.
 */
export async function serializeCartLines(
  edges: { node: CartLineNode }[],
  locale: string,
  discountData?: { bfcm: DiscountData; evergreen: DiscountData },
  skipIncompatibleItemsRemoval = false,
): Promise<SerializedCartItem[]> {
  // Handle incompatible items first (e.g., remove duplicate subscription-1 items)
  // Get fresh edges after removing incompatible items
  let cartItems = edges;

  if (!skipIncompatibleItemsRemoval) {
    try {
      cartItems = await handleIncompatibleItems(edges);
    } catch (error) {
      console.error('Error handling incompatible items:', error);
      cartItems = edges; // Fallback to original edges
    }
  }

  // Fetch all product data in a single API call using fresh edges
  const {
    translations: translationsMap,
    variants: _variantsSetMap,
    pdpUrls: pdpUrlsMap,
    ageRanges: ageRangesMap,
  } = await fetchProductData(cartItems, locale);

  // Create a Map for O(1) node lookups by ID
  const nodeMap = new Map<string, CartLineNode>();
  cartItems.forEach(({ node }) => {
    nodeMap.set(node.id, node);
  });

  const addonItems = new Set<string>();
  const parentToAddons = new Map<string, string[]>();
  const assignedAddons = new Set<string>(); // Track which addons have been assigned

  // Pre-process attributes for all nodes to avoid repeated find() operations
  const nodeAttributesMap = new Map<string, Map<string, string>>();
  cartItems.forEach(({ node }) => {
    const attributes = node.attributes || [];
    nodeAttributesMap.set(node.id, createAttributesMap(attributes));
  });

  // Pre-process parent references to eliminate O(n²) operations
  const parentReferences = new Map<string, Set<string>>();
  const legacyReferences = new Map<string, Set<string>>();

  cartItems.forEach(({ node }) => {
    const attributesMap = nodeAttributesMap.get(node.id);
    if (!attributesMap) return;

    const getProperty = createGetPropertyFunction(attributesMap);
    const parentProductId = getProperty('parent_product_id');
    const linkedProductHandle = getProperty('linked_product_handle');

    if (parentProductId) {
      if (!parentReferences.has(parentProductId)) {
        parentReferences.set(parentProductId, new Set());
      }
      const parentRef = parentReferences.get(parentProductId);
      if (parentRef) {
        parentRef.add(node.id);
      }
    }

    if (linkedProductHandle) {
      if (!legacyReferences.has(linkedProductHandle)) {
        legacyReferences.set(linkedProductHandle, new Set());
      }
      const legacyRef = legacyReferences.get(linkedProductHandle);
      if (legacyRef) {
        legacyRef.add(node.id);
      }
    }
  });

  // Identify potential parents and link their addons
  cartItems.forEach(({ node }) => {
    const attributesMap = nodeAttributesMap.get(node.id);
    if (!attributesMap) return;

    // Check if this is a potential parent (has addons)
    const getProperty = createGetPropertyFunction(attributesMap);
    const isCoursePack = getProperty('is_course_pack') === 'true';
    const isSubscription = getProperty('is_playgenius_subscription') === 'true';

    // O(1) lookup instead of O(n) some() operations
    const hasExplicitParentReference = parentReferences.has(
      node.merchandise.id,
    );
    const hasLegacyParentReference = legacyReferences.has(
      node.merchandise.product?.handle || '',
    );

    // Use explicit parent reference as primary, fallback to legacy methods
    const hasAddons = hasExplicitParentReference || hasLegacyParentReference;

    if (isCoursePack || isSubscription || hasAddons) {
      // Find all potential addons for this parent
      cartItems.forEach(({ node: potentialAddon }) => {
        // Skip if this addon has already been assigned to another parent
        if (assignedAddons.has(potentialAddon.id)) return;

        if (isAddonForParent(potentialAddon, node, cartItems)) {
          addonItems.add(potentialAddon.id);
          assignedAddons.add(potentialAddon.id); // Mark as assigned
          if (!parentToAddons.has(node.id)) {
            parentToAddons.set(node.id, []);
          }
          const addons = parentToAddons.get(node.id);
          if (addons) {
            addons.push(potentialAddon.id);
          }
        }
      });
    }
  });

  // Create the serialized items with addons
  const result: SerializedCartItem[] = [];

  for (const { node } of cartItems) {
    // Skip addon items from main list
    if (addonItems.has(node.id)) continue;

    const addonIds = parentToAddons.get(node.id) || [];
    const addons = [];

    // Process addons
    for (const addonId of addonIds) {
      const addonNode = nodeMap.get(addonId);
      if (!addonNode) continue;

      const addonPrice = calculateDisplayPrice(addonNode);
      const promoDiscounts = applyPromoDiscounts(
        addonNode.merchandise.id,
        addonPrice,
        discountData,
      );
      const effectiveAddonPrice =
        promoDiscounts.bfcm.price ??
        promoDiscounts.evergreenDiscount.price ??
        addonPrice;
      const effectiveAddonSavings =
        promoDiscounts.bfcm.price?.savings ??
        promoDiscounts.evergreenDiscount.price?.savings ??
        addonPrice.savings;

      const addonBase = createSerializedCartItemBase({
        node: addonNode,
        itemPrice: effectiveAddonPrice,
        isAddon: true,
        parentAttributes: node.attributes || [],
        translationsMap,
        variantsSetMap: _variantsSetMap,
        pdpUrlsMap,
        ageRangesMap,
      });

      addons.push({
        ...addonBase,
        ...(effectiveAddonSavings && { savings: effectiveAddonSavings }),
        ...(promoDiscounts.bfcm.hasDiscount && { hasBfcmDiscount: true }),
        ...(promoDiscounts.evergreenDiscount.hasDiscount && {
          hasEvergreenDiscount: true,
        }),
      });
    }

    const itemPrice = calculateDisplayPrice(node);
    const promoDiscounts = applyPromoDiscounts(
      node.merchandise.id,
      itemPrice,
      discountData,
    );
    const effectiveItemPrice =
      promoDiscounts.bfcm.price ??
      promoDiscounts.evergreenDiscount.price ??
      itemPrice;
    const effectiveSavings =
      promoDiscounts.bfcm.price?.savings ??
      promoDiscounts.evergreenDiscount.price?.savings ??
      itemPrice.savings;

    result.push({
      ...createSerializedCartItemBase({
        node,
        itemPrice: effectiveItemPrice,
        isAddon: false,
        translationsMap,
        variantsSetMap: _variantsSetMap,
        pdpUrlsMap,
        ageRangesMap,
      }),
      addons,
      savings: effectiveSavings,
      ...(promoDiscounts.bfcm.hasDiscount && { hasBfcmDiscount: true }),
      ...(promoDiscounts.evergreenDiscount.hasDiscount && {
        hasEvergreenDiscount: true,
      }),
    });
  }

  return result;
}
